/* todo : might abstract to @skv/state if other apps need it */

import * as React from "react";
import { useThunkReducer } from "@skv/state";

export interface AuthenticatedUserState {
  applicationCount: number;
  email: string | null;
  emailAlertCount: number;
  experienceCount: number;
  favouriteCount: number;
  isAuthenticated: boolean;
}

const UserStateContext = React.createContext<
  AuthenticatedUserState | undefined
>(undefined);

// below is only used to get the dispatch type returned
const __DISPATCH__ = () => useThunkReducer(reducer, undefined);

interface UserDispatchContext {
  dispatch: ReturnType<typeof __DISPATCH__>[1];
  updateUser: (
    user:
      | Partial<AuthenticatedUserState>
      | ((user: AuthenticatedUserState) => AuthenticatedUserState)
  ) => void;
}

const UserDispatchContext = React.createContext<
  UserDispatchContext | undefined
>(undefined);

type Actions = {
  type: "UPDATE_USER";
  payload: Partial<AuthenticatedUserState>;
};

const reducer = (
  state: AuthenticatedUserState,
  action: Actions
): AuthenticatedUserState => {
  switch (action.type) {
    case "UPDATE_USER":
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};

export const UserProvider: React.FC<{
  user: AuthenticatedUserState;
}> = ({ user, children }) => {
  const [getState, dispatch] = useThunkReducer(reducer, user);

  const updateUser = React.useMemo(() => {
    function updateUser(
      cb: (user: AuthenticatedUserState) => AuthenticatedUserState
    ): void;
    function updateUser(user: Partial<AuthenticatedUserState>): void;
    function updateUser(user: any): void {
      if (typeof user === "function") {
        const nextState = user(getState()) as AuthenticatedUserState;
        dispatch({
          type: "UPDATE_USER",
          payload: nextState,
        });
        return;
      }

      dispatch({
        type: "UPDATE_USER",
        payload: user,
      });
    }

    return updateUser;
  }, [dispatch, getState]);

  return (
    <UserStateContext.Provider value={getState()}>
      <UserDispatchContext.Provider
        value={React.useMemo(() => {
          return { dispatch, updateUser };
        }, [dispatch, updateUser])}
      >
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
};

export const useUserState = (): AuthenticatedUserState => {
  const ctx = React.useContext(UserStateContext);

  if (!ctx) {
    throw Error("useUser hook used outside a UserStateContextProvider");
  }

  return ctx;
};

export const useUserDispatch = (): UserDispatchContext => {
  const ctx = React.useContext(UserDispatchContext);

  if (!ctx) {
    throw Error("useUserDispatch hook used outside a UserStateContextProvider");
  }

  return ctx;
};

export const useUser = () => {
  const state = useUserState();
  const dispatch = useUserDispatch();

  return [state, dispatch] as const;
};
